"use client";

import { useEffect, useRef, useState } from "react";
import { BookingCta } from "@/components/dubai/ui/BookingCta";
import { Card, CardContent } from "@/components/dubai/ui/card";

const timelineItems = [
  {
    period: "1 to 2 Business Days",
    title: "Licence",
    description: (
      <>
        We check your documents and submit the IFZA application. You sign the incorporation papers and the licence is issued once approved.

      </>
    ),
  },
  {
    period: "Day 3",
    title: "Visa",
    description: (
      <>
       The entry permit is applied for as soon as the licence is in place. Your visa is processed on the same working day.
      </>
    ),
  },
  {
    period: "Days 4-5",
    title: "Emirates ID and Medical",
    description:
      "You complete the medical test and give your Emirates ID biometrics in 2 working days. The Emirates ID is then issued.",
  },
  {
    period: "7 Days in Total",
    title: "Company Registration Complete",
    description: (
      <>
        Regulated activities, third-party approvals, incomplete documents or additional checks can extend the timeline.
      </>
    ),
  },
];

const PINNED_SCROLL_DELAY = 80;
const PINNED_SCROLL_MULTIPLIER = 10.5;

export const CompanyRegistrationTimelineSection = () => {
  const stageRef = useRef<HTMLDivElement>(null);
  const timelineViewportRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const stepMarkerRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const [progress, setProgress] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [railGeometry, setRailGeometry] = useState({ top: 40, height: 0 });
  const [maxScrollOffset, setMaxScrollOffset] = useState(0);

  useEffect(() => {
    const updateProgress = () => {
      const stage = stageRef.current;
      const viewport = timelineViewportRef.current;
      const timeline = timelineRef.current;
      const markers = stepMarkerRefs.current.slice(0, timelineItems.length);
      if (
        !stage ||
        !viewport ||
        !timeline ||
        markers.some((marker) => marker === null)
      ) {
        return;
      }

      const timelineTop = timeline.getBoundingClientRect().top;
      const centers = markers.map((marker) => {
        const rect = marker!.getBoundingClientRect();
        return rect.top + rect.height / 2 - timelineTop;
      });
      const start = centers[0];
      const end = centers[centers.length - 1];
      const isPinnedLayout = window.matchMedia("(min-width: 1024px)").matches;
      const nextMaxOffset = Math.max(
        0,
        timeline.scrollHeight - viewport.clientHeight,
      );
      let nextProgress: number;
      let nextActiveStep: number;

      if (isPinnedLayout) {
        nextProgress = Math.min(
          1,
          Math.max(
            0,
            (-stage.getBoundingClientRect().top - PINNED_SCROLL_DELAY) /
              Math.max(1, nextMaxOffset * PINNED_SCROLL_MULTIPLIER),
          ),
        );
        nextActiveStep = Math.min(
          timelineItems.length - 1,
          Math.floor(nextProgress * timelineItems.length),
        );
      } else {
        const activationLine = window.innerHeight * 0.72;
        nextProgress = Math.min(
          1,
          Math.max(
            0,
            (activationLine - (timelineTop + start)) / Math.max(1, end - start),
          ),
        );
        nextActiveStep = centers.reduce(
          (current, center, index) =>
            timelineTop + center <= activationLine ? index : current,
          0,
        );
      }

      setProgress((current) =>
        Math.abs(current - nextProgress) > 0.0001 ? nextProgress : current,
      );
      setActiveStep((current) =>
        current === nextActiveStep ? current : nextActiveStep,
      );
      setRailGeometry((current) =>
        Math.abs(current.top - start) > 0.5 ||
        Math.abs(current.height - (end - start)) > 0.5
          ? { top: start, height: end - start }
          : current,
      );
      setMaxScrollOffset((current) =>
        Math.abs(current - nextMaxOffset) > 0.5 ? nextMaxOffset : current,
      );
    };

    let frame = 0;
    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateProgress();
      });
    };

    updateProgress();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    [stageRef.current, timelineViewportRef.current, timelineRef.current].forEach(
      (element) => {
        if (element) resizeObserver.observe(element);
      },
    );
    let trackingFrame = 0;
    let stageIsNearViewport = false;
    const trackWhileNearStage = () => {
      if (!stageIsNearViewport) {
        trackingFrame = 0;
        return;
      }
      updateProgress();
      trackingFrame = window.requestAnimationFrame(trackWhileNearStage);
    };
    const stageObserver = new IntersectionObserver(([entry]) => {
      stageIsNearViewport = entry.isIntersecting;
      if (stageIsNearViewport && !trackingFrame) {
        trackingFrame = window.requestAnimationFrame(trackWhileNearStage);
      } else if (!stageIsNearViewport && trackingFrame) {
        window.cancelAnimationFrame(trackingFrame);
        trackingFrame = 0;
      }
    }, { rootMargin: "100% 0px" });
    if (stageRef.current) stageObserver.observe(stageRef.current);

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      resizeObserver.disconnect();
      stageObserver.disconnect();
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(trackingFrame);
    };
  }, []);

  return (
    <div
      ref={stageRef}
      className="relative bg-black lg:min-h-[calc(100vh+var(--timeline-scroll-distance))]"
      style={
        {
          "--timeline-scroll-distance": `${maxScrollOffset * PINNED_SCROLL_MULTIPLIER + PINNED_SCROLL_DELAY}px`,
        } as React.CSSProperties
      }
    >
      {/* overflow-x-clip, not overflow-hidden: the glow below is 929px tall and
          rotated 90deg, so its on-screen width is its height and it spills past
          a phone viewport on both sides. `clip` trims that horizontally while
          leaving overflow-y visible (the pinned timeline track translates
          vertically past this box) and, unlike `hidden`, creates no scroll
          container -- so `lg:sticky` on this same element still pins. */}
      <section className="relative z-10 isolate w-full overflow-x-clip bg-black px-4 py-[70px] sm:px-6 lg:sticky lg:top-0 lg:h-screen lg:px-8">
      <div className="pointer-events-none absolute inset-x-0 top-[101px] -z-10 mx-auto h-[929px] max-w-[1269px] rotate-90 rounded-[336px] bg-[radial-gradient(50%_50%_at_50%_50%,rgba(13,109,250,0.2)_0%,rgba(13,109,250,0)_68%)] blur-[12.5px]" />
      <div className="mx-auto flex w-full max-w-[1180px] flex-col items-start gap-14 lg:h-full lg:flex-row">
        <div className="flex w-full min-w-0 flex-col items-start gap-[23.2px] pt-16 lg:flex-[0.75] lg:pt-[140px]">
          <div className="inline-flex items-center gap-[7.19px] rounded-[999px] bg-[#0061FE1a] px-[13.6px] py-[8.3px]">
            <img
              src="/dubai/formation.svg"
              aria-hidden="true"
              alt=""
              className="h-3.5 w-3.5 shrink-0"
            />
            <span className="font-suisse-intl text-[11.4px] font-extrabold leading-[17px] tracking-[1.36px] text-[#5799fb]">
              FAST FORMATION
            </span>
          </div>
          <div className="flex w-full max-w-[521.14px] flex-col items-start font-space-grotesk text-[clamp(2.75rem,5.65vw,70px)] font-medium leading-none capitalize">
            <h2 className="m-0 text-white">
              7-Day IFZA
              <br />
              Company
              <br />
              Registration
              <br />
              <span className="text-[#6FA6FF]">Timeline</span>
            </h2>
          </div>
          <p className="w-full max-w-[784px] pt-[0.8px] font-suisse-intl text-[17.3px] leading-[31.1px] text-[#a0a4b3]">
            For a standard application with complete documents and{" "}
            <br className="hidden sm:block" />
            no additional regulatory approval, we target company{" "}
            <br className="hidden sm:block" />
            registration within 7 days.
          </p>
          <BookingCta
            className="h-auto min-h-[53.59px] gap-[7.99px] rounded-[999px] bg-[#0B3CFF] px-[23.2px] py-[16.3px] font-suisse-intl text-sm font-bold leading-5 text-white shadow-[0px_1px_2px_-1px_#0000001a,0px_1px_3px_#0000001a] hover:bg-[#0B3CFF]/90"
          >
            <span>Setup My IFZA Company</span>
            <svg
              aria-hidden="true"
              className="h-4 w-4 shrink-0"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M8 3.33 12.67 8 8 12.67M12.33 8H3.33"
                stroke="#ffffff"
                strokeWidth="1.33"
              />
            </svg>
          </BookingCta>
        </div>
        <div
          ref={timelineViewportRef}
          className="relative mt-2 w-full min-w-0 overflow-visible lg:max-h-[calc(100vh_-_140px)] lg:flex-1"
        >
          <div
            ref={timelineRef}
            className="relative flex w-full flex-col items-stretch gap-8 pb-20 pl-8 sm:pl-10"
            style={{
              transform: `translateY(-${maxScrollOffset * progress}px)`,
            }}
          >
          <div
            aria-hidden="true"
            className="absolute left-[5.29px] w-0.5 bg-[#070b081a]"
            style={{ top: railGeometry.top, height: railGeometry.height }}
          >
            <div
              className="w-full origin-top bg-[#6FA6FF] transition-transform duration-100 ease-linear will-change-transform"
              style={{ height: "100%", transform: `scaleY(${progress})` }}
            />
          </div>
          {timelineItems.map((item, index) => (
            <div
              key={item.period}
              aria-current={index === activeStep ? "step" : undefined}
              className="relative"
            >
              <span
                ref={(element) => {
                  stepMarkerRefs.current[index] = element;
                }}
                className={`absolute -left-8 top-1/2 z-10 h-3 w-3 -translate-y-1/2 rounded-full border-2 border-[#6FA6FF] transition-colors duration-200 sm:-left-10 ${
                  index <= activeStep ? "bg-[#6FA6FF]" : "bg-black"
                }`}
                aria-hidden="true"
              />
              <Card
                className={`relative h-auto rounded-[32px] border bg-[#0f1319] transition-[opacity,border-color,box-shadow] duration-300 lg:h-52 ${
                  index > activeStep
                    ? "border-[#070b0817] opacity-[0.42]"
                    : index === activeStep
                      ? "border-[#0061FE66] shadow-[0px_24px_60px_#070b081a]"
                      : "border-[#070b0817]"
                }`}
              >
                <CardContent
                  className={`flex flex-col items-start gap-2 p-5 ${
                    index === 0
                      ? "lg:gap-[5.7px] lg:px-5 lg:pb-[56.13px] lg:pt-5"
                      : index === 1
                        ? "lg:gap-[6.7px] lg:px-5 lg:pb-[56.13px] lg:pt-5"
                        : index === 2
                          ? "lg:gap-[6.8px] lg:px-5 lg:pb-[81.88px] lg:pt-5"
                          : "lg:gap-[6.4px] lg:p-5"
                  }`}
                >

                  <div className={`w-full ${index === 2 ? "lg:pt-[8.79px]" : index === 3 ? "lg:pt-[9.19px]" : "lg:pt-[8.89px]"}`}>
                       <span className="inline-flex items-start rounded-[999px] bg-[#0061FE1a] -ml-2 mb-2 px-3 py-1 font-suisse-intl text-[11.2px] font-extrabold leading-[16.8px] text-[#0061FE]">
                    {item.period}
                  </span>
                    <h3 className="font-suisse-intl text-[20.8px] font-normal leading-[31.2px] text-white">
                      {item.title}
                    </h3>
                  </div>
                  <p className={`w-full font-suisse-intl text-[14.7px] leading-[25.76px] text-[#585d70] ${index < 3 ? "pb-[0.63px]" : ""}`}>
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            </div>
          ))}
          </div>
        </div>
      </div>
      </section>
    </div>
  );
};
